import { Message } from './chatService';

export interface Session {
  id: string;
  name: string;
  messages: Message[];
  createdAt: number;
  updatedAt: number;
}

const STORAGE_KEY = 'poker_chat_sessions';
const MAX_SESSIONS = 10;

class SessionService {
  /**
   * Generate unique session ID
   */
  private generateId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Load all sessions from localStorage
   */
  loadSessions(): Session[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const sessions: Session[] = JSON.parse(stored);

      // Convert timestamp strings back to Date objects in messages
      return sessions.map(session => ({
        ...session,
        messages: session.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      }));
    } catch (error) {
      console.error('Failed to load sessions:', error);
      return [];
    }
  }

  /**
   * Save all sessions to localStorage
   */
  private saveSessions(sessions: Session[]): boolean {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
      return true;
    } catch (error) {
      console.error('Failed to save sessions:', error);
      return false;
    }
  }

  /**
   * Create a new session
   */
  createSession(name?: string): Session {
    const sessions = this.loadSessions();

    const newSession: Session = {
      id: this.generateId(),
      name: name || `Chat ${sessions.length + 1}`,
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    // Limit number of sessions
    if (sessions.length >= MAX_SESSIONS) {
      // Remove oldest session
      sessions.sort((a, b) => a.updatedAt - b.updatedAt);
      sessions.shift();
    }

    sessions.push(newSession);
    this.saveSessions(sessions);

    return newSession;
  }

  /**
   * Get a specific session by ID
   */
  getSession(id: string): Session | null {
    const sessions = this.loadSessions();
    return sessions.find(s => s.id === id) || null;
  }

  /**
   * Update a session
   */
  updateSession(id: string, messages: Message[]): boolean {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === id);

    if (index === -1) return false;

    sessions[index].messages = messages;
    sessions[index].updatedAt = Date.now();

    return this.saveSessions(sessions);
  }

  /**
   * Rename a session
   */
  renameSession(id: string, name: string): boolean {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === id);

    if (index === -1) return false;

    sessions[index].name = name;
    sessions[index].updatedAt = Date.now();

    return this.saveSessions(sessions);
  }

  /**
   * Delete a session
   */
  deleteSession(id: string): boolean {
    const sessions = this.loadSessions();
    const filtered = sessions.filter(s => s.id !== id);

    if (filtered.length === sessions.length) return false;

    return this.saveSessions(filtered);
  }

  /**
   * Clear all sessions
   */
  clearAllSessions(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear sessions:', error);
    }
  }
}

export const sessionService = new SessionService();
